import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../hooks/useRTL';
import {
  Mail,
  Phone,
  ChevronRight,
  Heart,
  ExternalLink,
  ChevronDown,
  ChevronLeft,
  Newspaper,
  User,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Instagram from '../../assets/Instagram';
import Facebook from '../../assets/Facebook';
import Tiktok from '../../assets/Tiktok';
import logo from '../../assets/BusinessLogo.png';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';

// Memoized social media icons
const SocialIcon = React.memo(
  ({ Icon, name, link, width = 32, height = 32 }) => {
    return (
      <motion.a
        whileHover={{ y: -8, scale: 1.2 }}
        href={link}
        className='flex items-center'
        aria-label={name}>
        <div className='bg-transparent p-3 rounded-full'>
          <Icon width={width} height={height} fill='#ffffff' />
        </div>
      </motion.a>
    );
  }
);

// First, add this animation variant at the top of your file with other variants
const logoVariants = {
  initial: {
    opacity: 1,
    scale: 1,
    filter: 'drop-shadow(0 0 0px rgba(255,255,255,0.3))',
  },
  animate: {
    opacity: 1,
    scale: 1,
    filter: [
      'drop-shadow(0 0 10px rgba(255,255,255,0.3))',
      'drop-shadow(0 0 15px rgba(255,255,255,0.6))',
      'drop-shadow(0 0 10px rgba(255,255,255,0.3))',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
  hover: {
    scale: 1.1,
    filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.8))',
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

const Footer = ({ ThanksPage }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const { t } = useTranslation();
  const isRTL = useRTL();
  const [hoveredLink, setHoveredLink] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('social');
  const [loading, setLoading] = useState(false);
  const [didJoinNewsletter, setDidJoinNewsletter] = useState(false);

  const navigate = useNavigate();

  // Memoized validation function
  const validateForm = useCallback(() => {
    if (!name.trim()) {
      toast.error(t('errors.nameRequired'), {
        icon: '⚠️',
        position: 'top-center',
        duration: 3000,
      });
      return false;
    }
    if (!email.trim()) {
      toast.error(t('errors.emailRequired'), {
        icon: '⚠️',
        position: 'top-center',
        duration: 3000,
      });
      return false;
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      toast.error(t('errors.invalidEmail'), {
        icon: '⚠️',
        position: 'top-center',
        duration: 3000,
      });
      return false;
    }
    return true;
  }, [name, email, t]);

  // Share functionality
  // const handleShare = useCallback(async () => {
  //   try {
  //     if (navigator.share) {
  //       await navigator.share({
  //         title: 'TheBooker',
  //         text: t('footer.shareText'),
  //         url: window.location.origin,
  //       });
  //     } else {
  //       // Fallback copy to clipboard
  //       await navigator.clipboard.writeText(window.location.origin);
  //       toast.success(t('footer.linkCopied'));
  //     }
  //   } catch (error) {
  //     console.error('Error sharing:', error);
  //   }
  // }, [t]);

  const joinNewsLetter = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      try {
        const _base_url = 'api';
        const response = await fetch(
          `https://${_base_url}.thebooker.online/api/NewsLetter`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, name }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Newsletter subscription failed');
        }

        setDidJoinNewsletter(true);
        toast.success(t('footer.subscription_success'), {
          icon: '✨',
          position: 'top-center',
          duration: 4000,
        });

        // Send welcome email
        await emailjs.send(
          'service_h4br5is',
          'template_3bi0wub',
          { email, name },
          { publicKey: 'grg76XmeXnwqwv5cy' }
        );

        navigate('/thanks');
      } catch (error) {
        console.error('Newsletter error:', error);
        toast.error(error.message || t('footer.errors.generic'), {
          icon: '⚠️',
          position: 'top-center',
          duration: 4000,
        });
      } finally {
        setLoading(false);
      }
    },
    [email, name, validateForm, navigate, t]
  );

  // Memoized animation variants
  const animationVariants = useMemo(
    () => ({
      tabVariants: {
        inactive: { opacity: 0.6, scale: 0.95 },
        active: { opacity: 1, scale: 1 },
      },
      contentVariants: {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
      },
      inputVariants: {
        focus: { scale: 1.02, transition: { duration: 0.2 } },
        blur: { scale: 1, transition: { duration: 0.2 } },
      },
    }),
    []
  );

  // Lazy load images
  useEffect(() => {
    const img = new Image();
    img.src = logo;

    if (ThanksPage) setDidJoinNewsletter(true);
  }, [ThanksPage]);

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  return (
    <>
      {/* Desktop Footer */}
      <motion.footer
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='bg-booker text-white p-8 font-hurme min-w-full rounded-t-[8vh] hidden md:block'
        role='contentinfo'
        aria-label={t('footer.mainFooter')}>
        <div className='container mx-auto'>
          {/* Top Section - Enhanced */}
          <div className='flex flex-col md:flex-row justify-between items-center mb-16'>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className='flex items-center mb-4 md:mb-0'>
              <motion.img
                src={logo}
                alt='TheBooker logo'
                className={`${isRTL ? 'ml-2' : 'mr-2'} h-24`}
                variants={logoVariants}
                initial='initial'
                animate='animate'
                whileHover='hover'
                loading='lazy'
              />
            </motion.div>

            <div className='mb-4 md:mb-0'>
              <h3 className='font-hurmeBold text-bookerLightGray text-2xl mb-4'>
                {t('footer.contact_us')}
              </h3>
              <motion.p
                whileHover={{ x: isRTL ? -5 : 5 }}
                className='mb-2 flex items-center text-md'>
                <Mail
                  size={16}
                  className={`${isRTL ? 'ml-2' : 'mr-2'} text-bookerLightGray`}
                />
                <a
                  href='mailto:info@thebooker.app'
                  className='hover:underline text-white'>
                  info@thebooker.app
                </a>
              </motion.p>
              <motion.p
                whileHover={{ x: isRTL ? -5 : 5 }}
                className='flex items-center'>
                <Phone
                  size={16}
                  className={`${isRTL ? 'ml-2' : 'mr-2'} text-bookerLightGray`}
                />
                <a
                  style={{ color: 'white' }}
                  href='https://wa.me/96176624565'
                  rel='noreferrer'
                  target='_blank'
                  className='hover:underline'>
                  {t('footer.phone')}
                </a>
              </motion.p>
            </div>

            <div className='mb-4 md:mb-0'>
              <h3 className='font-hurmeBold text-bookerLightGray text-2xl mb-2'>
                {t('footer.follow_us')}
              </h3>
              <div
                className={`flex ${
                  isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'
                }`}>
                <SocialIcon
                  Icon={Instagram}
                  name={t('footer.instagram')}
                  link='#instagram'
                />
                <SocialIcon
                  Icon={Facebook}
                  name={t('footer.facebook')}
                  link='#facebook'
                />
                <SocialIcon
                  Icon={Tiktok}
                  name={t('footer.tiktok')}
                  link='#tiktok'
                />
              </div>
            </div>
          </div>

          {/* Middle Section with Navigation and Newsletter */}
          <div className='flex flex-row justify-between items-start mt-24'>
            {/* Navigation Links */}
            <nav
              className={`flex mt-5 ${
                isRTL ? 'space-x-reverse space-x-8' : 'space-x-8'
              } text-md`}>
              <motion.a
                href='/'
                whileHover={{ y: -2 }}
                style={{ color: 'white' }}
                className='hover:underline'>
                {t('footer.home')}
              </motion.a>
              <motion.a
                href='https://thebooker.online/#business'
                whileHover={{ y: -2 }}
                style={{ color: 'white' }}
                className='hover:underline'>
                {t('footer.pricings')}
              </motion.a>
              <div
                className='relative'
                onMouseEnter={() => {
                  setHoveredLink('blogs');
                  handleDropdownToggle();
                }}
                onMouseLeave={() => {
                  setHoveredLink(null);
                  setShowDropdown(false);
                }}>
                <Link
                  to='#c'
                  className={`text-white font-hurmeSemiBold pb-2 relative flex items-center ${
                    isRTL ? '-mr-2 ml-2' : 'ml-2 -mr-2'
                  }`}>
                  {t('nav.blogs')}
                  <ChevronDown size={16} className='ml-1' />
                  <span
                    className={`absolute bottom-0 ${
                      isRTL ? 'right-0' : 'left-0'
                    } w-full h-0.5 bg-white transform transition-all duration-300 ${
                      hoveredLink === 'blogs' ? 'scale-x-100' : 'scale-x-0'
                    } ${isRTL ? 'origin-right' : 'origin-left'}`}></span>
                </Link>
                <div
                  className={`absolute ${
                    isRTL ? 'right-0' : 'left-0'
                  } mt-0 bg-booker border border-bookerGray border-opacity-30 rounded-lg shadow-lg py-2 w-[280px] z-50 transform transition-all duration-300 ease-in-out origin-top ${
                    showDropdown
                      ? 'scale-y-100 opacity-100'
                      : 'scale-y-0 opacity-0'
                  }`}>
                  <div className='px-2'>
                    <a
                      href='/blog/5-Benefits-of-having-a-system-for-personal-care-business'
                      target='_blank'
                      className='group flex items-center px-4 py-2 text-white text-sm hover:bg-teal-500/20 rounded-lg transition-all duration-200'>
                      <span className='flex-1'>{t('nav.blogs1')}</span>
                      <ChevronRight
                        size={16}
                        className={`opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                          isRTL ? 'transform rotate-180' : ''
                        }`}
                      />
                    </a>
                    <a
                      href='/blog/embracing-personal-care-together'
                      target='_blank'
                      className='group flex items-center px-4 py-2 text-white text-sm hover:bg-teal-500/20 rounded-lg transition-all duration-200'>
                      <span className='flex-1'>{t('nav.blogs2')}</span>
                      <ChevronRight
                        size={16}
                        className={`opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                          isRTL ? 'transform rotate-180' : ''
                        }`}
                      />
                    </a>
                  </div>
                </div>
              </div>
            </nav>

            {/* Email Subscription */}
            <div
              className={`flex flex-col items-center -mt-9 ${
                isRTL ? 'ml-24' : 'mr-24 '
              }`}>
              <h3
                className={`font-hurmeBold text-bookerLightGray text-2xl mb-4 ${
                  isRTL ? '-mr-14' : '-ml-14 '
                }`}>
                {t('footer.join_newsletter')}
              </h3>
              {!didJoinNewsletter ? (
                <div className='flex flex-col gap-4'>
                  <div className='flex items-center'>
                    <input
                      type='text'
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('footer.name')}
                      className={`py-1 px-4 bg-transparent border-b border-white/30 outline-none focus:ring-0 focus:border-white w-72 placeholder-white/50 ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className='flex flex-col gap-4'>
                    <input
                      type='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('footer.email')}
                      className={`py-1 px-4 bg-transparent border-b border-white/30 outline-none focus:ring-0 focus:border-white w-72 placeholder-white/50 ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                      required
                      disabled={loading}
                    />
                    <motion.button
                      type='submit'
                      onClick={joinNewsLetter}
                      disabled={loading}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className='bg-bookerGray text-white px-4 py-1 rounded-xl transition text-md w-32 flex items-center justify-center self-end'>
                      {loading ? (
                        <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                      ) : (
                        <>{t('footer.subscribe')}</>
                      )}
                    </motion.button>
                  </div>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className='text-center space-y-4'>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className='w-16 h-16 bg-white/10 rounded-full mx-auto flex items-center justify-center'>
                    <svg
                      className='w-8 h-8 text-white'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'>
                      <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8 }}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                  </motion.div>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className='text-white/90 text-lg'>
                    {t('footer.subscription_success')}
                  </motion.p>
                </motion.div>
              )}
            </div>

            {/* Privacy and Terms  */}
            <div
              className={`flex flex-col items-start text-lg my-30 ml-2 sticky  ${
                isRTL ? 'left-[12rem]' : 'right-[12rem]'
              }`}>
              <motion.a
                style={{ color: 'white' }}
                whileHover={{ x: isRTL ? -3 : 3 }}
                href='/privacypolicy'
                className='hover:underline mb-1'>
                {t('footer.privacy_policy')}
              </motion.a>
              <div className='w-full h-px bg-white/30 my-1'></div>
              <motion.a
                style={{ color: 'white' }}
                whileHover={{ x: isRTL ? -3 : 3 }}
                href='/termsofuse'
                className='hover:underline mt-1'>
                {t('footer.terms_of_use')}
              </motion.a>
            </div>
          </div>

          {/* Copyright at bottom right */}
          <div
            className={`flex ${
              isRTL ? 'justify-start' : 'justify-end'
            } mt-8 text-sm text-white/80`}>
            {/* Copyright © {new Date().getFullYear()}. All rights reserved. */}
            {t('footer.copyright')}
          </div>
        </div>
      </motion.footer>

      {/* Footer*/}
      <motion.footer
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='bg-teal-600 text-white font-hurme min-w-full rounded-t-[5vh] block md:hidden'>
        {/* Header */}
        <div className='bg-gradient-to-r from-teal-700 via-teal-600 to-teal-500 p-4 flex justify-center items-center'>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className='flex items-center'>
            <motion.img
              src={logo}
              alt='TheBooker logo'
              className='h-16'
              variants={logoVariants}
              initial='initial'
              animate='animate'
              whileHover='hover'
            />
          </motion.div>
        </div>

        {/* Interactive Tab Navigation */}
        <div className='flex justify-around p-2 bg-teal-700'>
          <motion.div
            variants={animationVariants.tabVariants}
            animate={activeTab === 'social' ? 'active' : 'inactive'}
            whileTap={{ scale: 0.9 }}
            onClick={() => setActiveTab('social')}
            className={`flex flex-col items-center p-2 ${
              activeTab === 'social' ? 'border-b-2 border-white' : ''
            }`}>
            <Heart size={20} />
            <span className='text-xs mt-1'>{t('footer.connect')}</span>
          </motion.div>

          <motion.div
            variants={animationVariants.tabVariants}
            animate={activeTab === 'nav' ? 'active' : 'inactive'}
            whileTap={{ scale: 0.9 }}
            onClick={() => setActiveTab('nav')}
            className={`flex flex-col items-center p-2 ${
              activeTab === 'nav' ? 'border-b-2 border-white' : ''
            }`}>
            <ExternalLink size={20} />
            <span className='text-xs mt-1'>{t('footer.explore')}</span>
          </motion.div>

          <motion.div
            variants={animationVariants.tabVariants}
            animate={activeTab === 'contact' ? 'active' : 'inactive'}
            whileTap={{ scale: 0.9 }}
            onClick={() => setActiveTab('contact')}
            className={`flex flex-col items-center p-2 ${
              activeTab === 'contact' ? 'border-b-2 border-white' : ''
            }`}>
            <User size={20} />
            <span className='text-xs mt-1'>{t('footer.contact')}</span>
          </motion.div>

          <motion.div
            variants={animationVariants.tabVariants}
            animate={activeTab === 'newsletter' ? 'active' : 'inactive'}
            whileTap={{ scale: 0.9 }}
            onClick={() => setActiveTab('newsletter')}
            className={`flex flex-col items-center p-2 ${
              activeTab === 'newsletter' ? 'border-b-2 border-white' : ''
            }`}>
            <Newspaper size={20} />
            <span className='text-xs mt-1'>{t('footer.Newsletter')}</span>
          </motion.div>
        </div>

        {/* Content Area - Changes based on active tab */}
        <div className='p-4'>
          <AnimatePresence mode='wait'>
            {activeTab === 'social' && (
              <motion.div
                key='social'
                variants={animationVariants.contentVariants}
                initial='hidden'
                animate='visible'
                exit='hidden'
                className='py-4'>
                <h3 className='font-hurmeBold text-bookerLightGray text-xl mb-4 text-center'>
                  {t('footer.follow_us')}
                </h3>
                <div
                  className={`flex justify-center ${
                    isRTL ? 'space-x-reverse space-x-8' : 'space-x-8'
                  } py-4`}>
                  <SocialIcon
                    Icon={Instagram}
                    name={t('footer.instagram')}
                    link='#instagram'
                  />
                  <SocialIcon
                    Icon={Facebook}
                    name={t('footer.facebook')}
                    link='#facebook'
                  />
                  <SocialIcon
                    Icon={Tiktok}
                    name={t('footer.tiktok')}
                    link='#tiktok'
                  />
                </div>

                <div className='flex justify-center mt-6'>
                  <div className='w-16 h-1 bg-white/30 rounded-full'></div>
                </div>

                <div className='mt-6 text-center'>
                  <p className='text-sm text-white/70'>
                    {t('footer.connect_with_us')}
                  </p>
                </div>
              </motion.div>
            )}

            {activeTab === 'nav' && (
              <motion.div
                key='nav'
                variants={animationVariants.contentVariants}
                initial='hidden'
                animate='visible'
                exit='hidden'
                className='py-4'>
                <h3 className='font-hurmeBold text-bookerLightGray text-xl mb-4 text-center'>
                  {t('footer.explore_thebooker')}
                </h3>

                <div className='flex flex-col space-y-4 mt-6'>
                  <motion.a
                    href='/'
                    whileHover={{ x: isRTL ? -10 : 10 }}
                    className='flex justify-between items-center py-3 px-4 bg-teal-700/50 rounded-xl'>
                    <span className='font-hurmeBold'>{t('nav.home')}</span>
                    <ChevronRight
                      size={18}
                      className={isRTL ? 'transform rotate-180' : ''}
                    />
                  </motion.a>

                  <motion.a
                    href='https://thebooker.online/#business'
                    whileHover={{ x: isRTL ? -10 : 10 }}
                    className='flex justify-between items-center py-3 px-4 bg-teal-700/50 rounded-xl'>
                    <span className='font-hurmeBold'>{t('nav.pricing')}</span>
                    <ChevronRight
                      size={18}
                      className={isRTL ? 'transform rotate-180' : ''}
                    />
                  </motion.a>

                  <motion.div
                    className='py-3 px-4 bg-teal-700/50 rounded-xl'
                    onClick={() => setShowDropdown(!showDropdown)}>
                    <div className='flex justify-between items-center'>
                      <span className='font-hurmeBold'>{t('nav.blogs')}</span>
                      {!isRTL ? (
                        <ChevronRight
                          size={18}
                          className={`transform transition-transform ${
                            showDropdown ? 'rotate-90' : ''
                          }`}
                        />
                      ) : (
                        <ChevronLeft
                          size={18}
                          className={`transform transition-transform ${
                            showDropdown ? 'rotate-90' : ''
                          }`}
                        />
                      )}
                    </div>

                    {showDropdown && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className='mt-3 space-y-2'>
                        <a
                          href='/blog/5-Benefits-of-having-a-system-for-personal-care-business'
                          className='group flex items-center py-2 px-4 text-white/80 hover:text-white bg-teal-700/30 rounded-lg transition-all duration-200'>
                          <span className='flex-1'>{t('nav.blogs1')}</span>
                          <ChevronRight
                            size={16}
                            className='opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                          />
                        </a>
                        <a
                          href='/blog/embracing-personal-care-together'
                          className='group flex items-center py-2 px-4 text-white/80 hover:text-white bg-teal-700/30 rounded-lg transition-all duration-200'>
                          <span className='flex-1'>{t('nav.blogs2')}</span>
                          <ChevronRight
                            size={16}
                            className='opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                          />
                        </a>
                      </motion.div>
                    )}
                  </motion.div>
                </div>

                <div className='mt-6 flex justify-between px-2'>
                  <motion.a
                    whileHover={{ y: -3 }}
                    href='/privacy'
                    className='text-sm text-white/70 hover:text-white'>
                    {t('footer.privacy_and_terms')}
                  </motion.a>
                  <div className='w-px h-4 bg-white/30 self-center'></div>
                  <motion.a
                    whileHover={{ y: -3 }}
                    href='/terms'
                    className='text-sm text-white/70 hover:text-white'>
                    {t('footer.terms_and_conditions')}
                  </motion.a>
                </div>
              </motion.div>
            )}

            {activeTab === 'contact' && (
              <motion.div
                key='contact'
                variants={animationVariants.contentVariants}
                initial='hidden'
                animate='visible'
                exit='hidden'
                className='py-4'>
                <h3 className='font-hurmeBold text-bookerLightGray text-xl mb-4 text-center'>
                  {t('footer.contact_us')}
                </h3>

                <div className='bg-teal-700/30 rounded-xl p-6 mt-4'>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className='flex flex-col items-center mb-6'>
                    <div className={`bg-booker p-3 rounded-full mb-3`}>
                      <Mail size={24} className={`text-white `} />
                    </div>
                    <p className='text-center'>
                      <a
                        href='mailto:info@thebooker.app'
                        className='underline decoration-dashed underline-offset-4'>
                        info@thebooker.app
                      </a>
                    </p>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className='flex flex-col items-center'>
                    <div className='bg-teal-500 p-3 rounded-full mb-3'>
                      <Phone size={24} className='text-white' />
                    </div>
                    <p className='text-center'>
                      <a
                        href='https://wa.me/96176624565'
                        rel='noreferrer'
                        target='_blank'
                        className='underline decoration-dashed underline-offset-4'>
                        {t('footer.phone')}
                      </a>
                    </p>
                  </motion.div>
                </div>

                <div className='mt-6 text-center text-sm text-white/70'>
                  <p>{t('footer.available_hours')}</p>
                </div>
              </motion.div>
            )}

            {activeTab === 'newsletter' && (
              <motion.div
                key='newsletter'
                variants={animationVariants.contentVariants}
                initial='hidden'
                animate='visible'
                exit='hidden'
                className='py-4 '>
                <h3 className='font-hurmeBold text-bookerLightGray text-xl mb-4 text-center'>
                  {t('footer.join_newsletter')}
                </h3>

                <div className='mt-4 bg-gradient-to-b from-teal-700/50 to-teal-600/20 p-5 rounded-xl '>
                  {!didJoinNewsletter ? (
                    <div className='flex flex-col gap-4'>
                      <div className='flex items-center'>
                        <input
                          type='text'
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t('footer.name')}
                          className={`py-1 px-4 bg-transparent border-b border-white/30 outline-none focus:ring-0 focus:border-white w-full max-w-xs placeholder-white/50 ${
                            isRTL ? 'text-right' : 'text-left'
                          }`}
                          required
                          disabled={loading}
                        />
                      </div>
                      <div className='flex items-center'>
                        <input
                          type='email'
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t('footer.email')}
                          className={`py-1 px-4 bg-transparent border-b border-white/30 outline-none focus:ring-0 focus:border-white w-full max-w-xs placeholder-white/50 ${
                            isRTL ? 'text-right' : 'text-left'
                          }`}
                          required
                          disabled={loading}
                        />
                      </div>

                      <motion.button
                        type='submit'
                        onClick={joinNewsLetter}
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className='bg-white text-booker font-hurmeBold py-3 px-4 rounded-xl transition w-full shadow-lg flex items-center justify-center'>
                        {loading ? (
                          <div className='w-5 h-5 border-2 border-booker border-t-transparent rounded-full animate-spin'></div>
                        ) : (
                          t('footer.subscribe_button')
                        )}
                      </motion.button>
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className='text-center py-6'>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200 }}
                        className='w-16 h-16 bg-white/10 rounded-full mx-auto flex items-center justify-center'>
                        <svg
                          className='w-8 h-8 text-white'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'>
                          <motion.path
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.8 }}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M5 13l4 4L19 7'
                          />
                        </svg>
                      </motion.div>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className='text-white/90 mt-4'>
                        {t('footer.subscription_success')}
                      </motion.p>
                    </motion.div>
                  )}
                </div>

                <div className='mt-6 flex justify-center'>
                  <div className='text-xs text-center text-white/60 max-w-xs'>
                    {t('footer.subscribe_terms')}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Copyright at bottom */}
        <div className='p-4 text-center text-xs text-white/60 border-t border-white/10'>
          {t('footer.copyright')}
        </div>
      </motion.footer>
    </>
  );
};

export default React.memo(Footer);
